import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
    HomeIcon,
    BuildingOffice2Icon,
    BuildingOfficeIcon,
    BanknotesIcon,
    DocumentTextIcon,
    UserPlusIcon,
    UserMinusIcon,
    UsersIcon,
    XMarkIcon,
    ChevronLeftIcon,
    ChevronRightIcon,
    FolderIcon,
    ChevronDownIcon,
    ChevronUpIcon,
    ShieldCheckIcon,
    RectangleGroupIcon,
    RectangleStackIcon
} from '@heroicons/react/24/outline';
import { Roles } from '../../constants/roles';
import logoImage from '../../assets/logo.png';

interface SidebarProps {
    isOpen: boolean;
    setIsOpen: (isOpen: boolean) => void;
    isCollapsed: boolean;
    setIsCollapsed: (isCollapsed: boolean) => void;
}

interface NavigationItem {
    name: string;
    href: string;
    icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
    children?: NavigationItem[];
    requiresAdmin?: boolean;
}

interface NavigationSection {
    title?: string;
    items: NavigationItem[];
    requiresAdmin?: boolean;
}

const navigationSections: NavigationSection[] = [
    {
        // Main section - no title
        items: [
            { name: 'Dashboard', href: '/', icon: HomeIcon },
            { name: 'Onboarding', href: '/onboarding', icon: UserPlusIcon },
            { name: 'Offboarding', href: '/offboarding', icon: UserMinusIcon },
            { name: 'Employees', href: '/employees', icon: UsersIcon },
        ]
    },
    {
        title: 'Setup',
        items: [
            { name: 'Locations', href: '/locations', icon: BuildingOffice2Icon },
            { name: 'Departments', href: '/departments', icon: BuildingOfficeIcon },
            { name: 'Payroll Companies', href: '/payroll-companies', icon: BanknotesIcon },
            {
                name: 'Templates',
                href: '#', // Changed to prevent link conflict
                icon: DocumentTextIcon,
                children: [
                    { name: 'All Templates', href: '/templates', icon: DocumentTextIcon },
                    { name: 'Task Templates', href: '/task-templates', icon: RectangleStackIcon },
                    { name: 'Categories', href: '/template-categories', icon: FolderIcon },
                ]
            },
        ]
    },
    {
        title: 'Admin',
        requiresAdmin: true,
        items: [
            { name: 'Teams', href: '/teams', icon: RectangleGroupIcon },
            { name: 'System Users', href: '/system-users', icon: ShieldCheckIcon },
        ]
    },
];

export default function Sidebar({ isOpen, setIsOpen, isCollapsed, setIsCollapsed }: SidebarProps) {
    const location = useLocation();
    const { user } = useAuth();

    // 1. Initialize state based on current URL so menus stay open on refresh
    const [expandedMenus, setExpandedMenus] = useState<Record<string, boolean>>(() => {
        const initialExpanded: Record<string, boolean> = {};
        navigationSections.forEach(section => {
            section.items.forEach(item => {
                if (item.children?.some(child => location.pathname === child.href)) {
                    initialExpanded[item.name] = true;
                }
            });
        });
        return initialExpanded;
    });

    const isAdmin = user?.roles?.includes(Roles.Administrator);
    const filteredSections = navigationSections.filter(section => !section.requiresAdmin || isAdmin);

    // 2. Exact match logic for focus
    const isActive = (path: string) => location.pathname === path;

    const toggleMenu = (name: string) => {
        setExpandedMenus(prev => ({
            ...prev,
            [name]: !prev[name]
        }));
    };

    return (
        <>
            {/* Mobile backdrop */}
            {isOpen && (
                <div
                    className="fixed inset-0 bg-gray-900 bg-opacity-50 z-20 lg:hidden"
                    onClick={() => setIsOpen(false)}
                />
            )}

            {/* Sidebar */}
            <aside
                className={`
          fixed top-0 left-0 z-30 h-screen flex flex-col bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 transition-all duration-300 ease-in-out
          ${isCollapsed ? 'w-20' : 'w-64'}
          ${isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        `}
            >
                {/* Logo and collapse button */}
                <div className="flex items-center justify-between h-16 px-4 flex-shrink-0 border-b border-gray-200 dark:border-gray-700">
                    {!isCollapsed && (
                        <div className="flex items-center space-x-3">
                            <img src={logoImage} alt="Logo" className="w-10 h-10 object-contain" />
                            <span className="text-l font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                                Boarding System
                            </span>
                        </div>
                    )}

                    {isCollapsed && (
                        <div className="flex items-center justify-center mx-auto">
                            <img src={logoImage} alt="Logo" className="w-10 h-10 object-contain" />
                        </div>
                    )}

                    {/* Desktop collapse button */}
                    <button
                        onClick={() => setIsCollapsed(!isCollapsed)}
                        className="hidden lg:block p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                    >
                        {isCollapsed ? (
                            <ChevronRightIcon className="w-5 h-5 text-gray-500" />
                        ) : (
                            <ChevronLeftIcon className="w-5 h-5 text-gray-500" />
                        )}
                    </button>

                    {/* Mobile close button */}
                    <button
                        onClick={() => setIsOpen(false)}
                        className="lg:hidden p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                    >
                        <XMarkIcon className="w-6 h-6 text-gray-500" />
                    </button>
                </div>

                {/* Navigation */}
                <nav className="flex-1 px-3 py-4 overflow-y-auto">
                    {filteredSections.map((section, sectionIndex) => (
                        <div key={section.title || `section-${sectionIndex}`} className={sectionIndex > 0 ? 'mt-6' : ''}>
                            {/* Section Titles */}
                            {section.title && !isCollapsed && (
                                <div className="px-3 mb-2">
                                    <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider">{section.title}</h3>
                                </div>
                            )}

                            <div className="space-y-1">
                                {section.items.map((item) => {
                                    const hasChildren = item.children && item.children.length > 0;
                                    const isExpanded = expandedMenus[item.name];

                                    // Check active states
                                    const isAnyChildActive = item.children?.some(child => isActive(child.href));
                                    // Parent is active if its own href matches OR a child is active
                                    const isParentActive = isActive(item.href) || isAnyChildActive;

                                    return (
                                        <div key={item.name}>
                                            {hasChildren ? (
                                                <>
                                                    <button
                                                        onClick={() => toggleMenu(item.name)}
                                                        className={`
                                            w-full flex items-center px-3 py-2.5 rounded-lg transition-all duration-200 group
                                            ${isParentActive
                                                                ? 'bg-gradient-to-r from-blue-500 to-indigo-600 text-white shadow-md' // Highlight stays even if expanded
                                                                : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                                                            }
                                            ${isCollapsed ? 'justify-center' : 'justify-between'}
                                        `}
                                                    >
                                                        <div className={`flex items-center ${isCollapsed ? '' : 'space-x-3'}`}>
                                                            <item.icon className={`w-6 h-6 flex-shrink-0 ${isParentActive ? 'text-white' : 'text-gray-500 group-hover:text-blue-600'}`} />
                                                            {!isCollapsed && <span className="font-medium text-sm">{item.name}</span>}
                                                        </div>
                                                        {!isCollapsed && (isExpanded ? <ChevronUpIcon className="w-4 h-4" /> : <ChevronDownIcon className="w-4 h-4" />)}
                                                    </button>

                                                    {/* Animated Sub-menu Wrapper */}
                                                    <div
                                                        className={`
                                            overflow-hidden transition-all duration-300 ease-in-out
                                            ${isExpanded && !isCollapsed ? 'max-h-96 opacity-100 mt-1' : 'max-h-0 opacity-0'}
                                        `}
                                                    >
                                                        <div className="ml-6 space-y-1 border-l-2 border-gray-100 dark:border-gray-700 pl-2">
                                                            {item.children?.map((child) => (
                                                                <Link
                                                                    key={child.name}
                                                                    to={child.href}
                                                                    onClick={() => setIsOpen(false)}
                                                                    className={`
                                                        flex items-center px-3 py-2 rounded-lg transition-all duration-200 text-sm
                                                        ${isActive(child.href)
                                                                            ? 'bg-blue-50 text-blue-700 font-bold dark:bg-blue-900/30 dark:text-blue-400'
                                                                            : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                                                                        }
                                                    `}
                                                                >
                                                                    <child.icon className={`w-5 h-5 mr-2 ${isActive(child.href) ? 'text-blue-700 dark:text-blue-400' : ''}`} />
                                                                    {child.name}
                                                                </Link>
                                                            ))}
                                                        </div>
                                                    </div>
                                                </>
                                            ) : (
                                                <Link
                                                    to={item.href}
                                                    onClick={() => setIsOpen(false)}
                                                    className={`
                                        flex items-center px-3 py-2.5 rounded-lg transition-all duration-200 group
                                        ${isActive(item.href)
                                                            ? 'bg-gradient-to-r from-blue-500 to-indigo-600 text-white shadow-md'
                                                            : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                                                        }
                                        ${isCollapsed ? 'justify-center' : 'space-x-3'}
                                    `}
                                                >
                                                    <item.icon className={`w-6 h-6 flex-shrink-0 ${isActive(item.href) ? 'text-white' : 'text-gray-500 group-hover:text-blue-600'}`} />
                                                    {!isCollapsed && <span className="font-medium text-sm">{item.name}</span>}
                                                </Link>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    ))}
                </nav>

            </aside>
        </>
    );
}
